import type { TicketStatus, TicketType } from '../api.ts';

/**
 * The words the Support screens use, and the checks a ticket passes before it
 * is sent. Plain module so both are tested; the components only render them.
 */

export const SUBJECT_MAX = 200;
export const BODY_MAX = 10_000;

export const TYPE_OPTIONS: readonly { readonly value: TicketType; readonly label: string }[] = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'suggestion', label: 'Suggestion' },
];

export const typeLabel = (type: TicketType): string =>
  TYPE_OPTIONS.find((option) => option.value === type)?.label ?? type;

/**
 * The customer's view of whose move it is. `awaiting_client` is the server's
 * name for "we have answered", and a customer reading "Awaiting client" would
 * reasonably think it meant one of *their* clients — the console's word for a
 * Microsoft 365 tenant.
 */
const CONSOLE_STATUS: Record<TicketStatus, string> = {
  awaiting_support: 'Awaiting support',
  awaiting_client: 'Reply from support',
  closed: 'Closed',
};

/** Staff read the server's own terms. In the platform area a client is an account. */
const PLATFORM_STATUS: Record<TicketStatus, string> = {
  awaiting_support: 'Awaiting support',
  awaiting_client: 'Awaiting client',
  closed: 'Closed',
};

export const consoleStatusLabel = (status: TicketStatus): string =>
  CONSOLE_STATUS[status] ?? status;

export const platformStatusLabel = (status: TicketStatus): string =>
  PLATFORM_STATUS[status] ?? status;

export const PLATFORM_FILTERS: readonly { readonly value: TicketStatus | 'all'; readonly label: string }[] =
  [
    { value: 'awaiting_support', label: 'Awaiting support' },
    { value: 'awaiting_client', label: 'Awaiting client' },
    { value: 'closed', label: 'Closed' },
    { value: 'all', label: 'All' },
  ];

export const countByStatus = (
  tickets: readonly { readonly status: TicketStatus }[],
  status: TicketStatus,
): number => tickets.filter((ticket) => ticket.status === status).length;

export const matchesFilter = (status: TicketStatus, filter: TicketStatus | 'all'): boolean =>
  filter === 'all' || status === filter;

export interface TicketErrors {
  readonly subject?: string;
  readonly body?: string;
}

const bodyProblem = (body: string): string | undefined => {
  if (body.trim() === '') return 'Write a message.';
  if (body.length > BODY_MAX) {
    return `The message is ${body.length.toLocaleString('en-GB')} characters; the limit is ${BODY_MAX.toLocaleString('en-GB')}.`;
  }
  return undefined;
};

/**
 * What stops a new ticket being sent. Checked on the trimmed subject, because
 * a subject of spaces is a subject nobody can find in a list.
 */
export const validateTicket = (draft: { subject: string; body: string }): TicketErrors => {
  const subject = draft.subject.trim();
  const errors: { subject?: string; body?: string } = {};
  if (subject === '') errors.subject = 'Give the ticket a subject.';
  else if (subject.length > SUBJECT_MAX) {
    errors.subject = `The subject is ${subject.length} characters; the limit is ${SUBJECT_MAX}.`;
  }
  const body = bodyProblem(draft.body);
  if (body !== undefined) errors.body = body;
  return errors;
};

export const validateReply = (body: string): TicketErrors => {
  const problem = bodyProblem(body);
  return problem === undefined ? {} : { body: problem };
};

export const hasErrors = (errors: TicketErrors): boolean =>
  errors.subject !== undefined || errors.body !== undefined;

const WHEN = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export const formatWhen = (iso: string): string => {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : WHEN.format(at);
};

/** Oldest first, whatever order the server sent: a thread reads top to bottom. */
export const oldestFirst = <T extends { readonly createdAt: string }>(messages: readonly T[]): T[] =>
  [...messages].sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
